'use client';

import { useEffect, useRef, useMemo } from 'react';
import type { Render } from '@/types/render';
import { setRendersForConcept } from '../model/renders-cache';

/**
 * Hydrate the TanStack Query renders cache with the concept-detail payload.
 *
 * This is reconcile point L1: the API answer is the source of truth and must
 * overwrite whatever a realtime push left in the cache. It therefore re-runs
 * whenever the payload DESCRIBES the renders differently — not only when the
 * set of ids changes. Keying on ids alone meant a refetch of the same renders
 * (exactly what the WS handler asks for after a render finishes) was skipped,
 * so nothing ever repaired a render the realtime path had left partial until
 * the page was reloaded.
 */
export function useInitializeRendersFromData(
  conceptId: string | undefined,
  renders: Record<string, Render[]> | undefined
) {
  const initializedConceptRef = useRef<string | null>(null);
  const previousRendersHashRef = useRef<string | null>(null);

  const rendersHash = useMemo(() => {
    if (!renders) return null;
    return Object.values(renders)
      .flatMap((rs) =>
        rs.map((r) =>
          [
            r.id,
            r.attributes.status,
            r.attributes.updated_at,
            r.relationships?.file?.meta?.url ?? r.attributes.file_url ?? '',
          ].join('|')
        )
      )
      .sort()
      .join(',');
  }, [renders]);

  useEffect(() => {
    if (!conceptId || !renders) return;

    const isSameConcept = initializedConceptRef.current === conceptId;
    const isSameRenders = previousRendersHashRef.current === rendersHash;
    if (isSameConcept && isSameRenders) return;

    const flat: Render[] = [];
    Object.values(renders).forEach((angleRenders) => {
      if (Array.isArray(angleRenders)) flat.push(...angleRenders);
    });

    setRendersForConcept(conceptId, flat);

    initializedConceptRef.current = conceptId;
    previousRendersHashRef.current = rendersHash;
  }, [conceptId, rendersHash, renders]);
}
